<script setup lang="ts">
import { debounce } from 'lodash';
import {
  Ref,
  nextTick,
  onBeforeUnmount,
  onMounted,
  computed,
  ref,
  watch,
} from 'vue';
import { useRoute } from 'vue-router';
import {
  ApplicationEvents,
  CaseClosed,
  CaseServiceInterface,
  CaseStatusService,
  CaseStorageInterface,
  CaseUpdated,
  CaseUpdateFailed,
  ContainerTypes,
  Contexts,
  CurrentCaseService,
  CurrentPageService,
  ErrorsListComponent,
  getContainer,
  getContext,
  MissingsListComponent,
  NamedBindings,
  SingleCaseComponent,
  TranslationService,
  TreeComponent,
  WarningsListComponent,
  MissingListCounter,
  CoreConfiguration,
  VariableHovered,
  LoggerInterface,
} from '@dandelion/core';
import { RESTCaseStorage } from '@dandelion/core/storages';
import StatusDisplayer from './StatusDisplayer.vue';
import { Messages } from '../enum/Messages';
import router from '../router';
import TemporaryPluginSvgs from './TemporaryPluginSvgs.vue';

const container = getContainer(Contexts.DEFAULT);
const caseStatusService = container.get(
  NamedBindings.CaseStatusService
) as CaseStatusService;
const currentCaseService = container.get(
  NamedBindings.CurrentCaseService
) as CurrentCaseService;
const currentPageService = container.get(
  NamedBindings.CurrentPageService
) as CurrentPageService;
const caseService = container.get(
  NamedBindings.CaseService
) as CaseServiceInterface;
const ts = container.get(
  NamedBindings.TranslationService
) as TranslationService;
const coreConfiguration = container.get(
  NamedBindings.CoreConfiguration
) as CoreConfiguration;
const logger = container.get(NamedBindings.Logger) as LoggerInterface;

let showErrors = ref(false);
let showWarnings = ref(false);
let showMissing = ref(false);
let showDefinition = ref(true);
let isFullscreen = ref(false);
let isStatusExpanded = ref(false);
let lockExpired = ref(false);
let varHoverEvent: string;
let caseUpdatedToken: string;
let caseUpdatedFailedToken: string;
let caseClosedToken: string;
let caseLockExpiringSoon: string;
let caseLockExpired: string;

const mapForStatus = caseStatusService.getStatusMap();
const route = useRoute();
let watchStopHandler = () => {};
const name = ref('');
const surname = ref('');
const isDebug = coreConfiguration.debug;
const lockCountdown: Ref<number | null> = currentCaseService.lockCountdown;
const lockCountdownProgress = computed(
  () => ((lockCountdown?.value ?? 60) / 60) * 100
);

if (!currentCaseService.isCaseOpened.value) {
  await currentCaseService.open(route.params.id as string);
}

const model = currentCaseService.getModel();
name.value = model.core?.personalData?.name;
surname.value = model.core?.personalData?.surname;

function toggleFullscreen() {
  isFullscreen.value = !isFullscreen.value;
}

function toggleStatusDisplayer() {
  isStatusExpanded.value = !isStatusExpanded.value;
  const sccContent = document.querySelector('.single-case-component-content');
  const saveBtn = document.querySelector('.save-case-btn');
  const closeBtn = document.querySelector('.close-case-btn');
  const sccClass = isDebug ? 'debug-view-main-reduced' : 'main-reduced';
  sccContent?.classList.toggle(sccClass, isStatusExpanded.value);
  saveBtn?.classList.toggle('main-reduced', isStatusExpanded.value);
  closeBtn?.classList.toggle('main-reduced', isStatusExpanded.value);
}

function validateInputs() {
  const singleCaseComponent = document.querySelector('.single-case-component');
  if (!singleCaseComponent) return;
  const numberInputs = singleCaseComponent.querySelectorAll(
    'input[type="number"][min][max]'
  ) as NodeListOf<HTMLInputElement>;
  numberInputs.forEach((input) => {
    const inputLabel =
      input.parentElement!.querySelector('.variableLabel')!.textContent!;
    const inputValue = parseFloat(input.value);
    const minValue = parseFloat(input.min);
    const maxValue = parseFloat(input.max);
    if (inputValue < minValue || inputValue > maxValue) {
      currentPageService.scrollToVariable(input.id);
      input.value = '';
      logger.logError(
        ts.t('minMaxError', {
          name: inputLabel,
          min: input.min,
          max: input.max,
          value: input.value,
        }),
        {}
      );
    }
  });
}

async function moveDescription() {
  showDefinition.value = !showDefinition.value;
  const definitionsPanel = document.getElementById('definitions-panel')!;
  const defPanelheight = definitionsPanel.clientHeight;
  const defTitle = document.getElementById(
    'footer-definition-title'
  ) as HTMLDivElement;
  if (showDefinition.value) {
    defTitle.style.transition = '0.3s';
    defTitle.style.transform = `translatey(-${defPanelheight}px)`;
  } else {
    defTitle.style.transform = `translatey(0px)`;
  }
}

async function changeDescription(variableName: string) {
  let descriptionElement: HTMLElement | null = null;

  // Try the 'wrapper' div first
  const element = document.getElementById(variableName + '-wrapper');
  let label;
  if (element) {
    descriptionElement = element.querySelector(':scope > .variableDescription') as HTMLElement | null;
    label = element.querySelector(':scope > .valueLabel > span')as HTMLElement;
       if (!label)
    label = element.querySelector(':scope > .radioDescription')as HTMLElement;

  }

  // If not found, try the fieldset directly
  if (!descriptionElement) {
    const fieldset = document.querySelector(
      `fieldset#${CSS.escape(variableName)}`
    ) as HTMLFieldSetElement | null;
    if (fieldset) {
      descriptionElement = fieldset.querySelector(':scope > .variableDescription') as HTMLElement | null;
      label = fieldset.querySelector(':scope > .groupDescription')as HTMLElement;

    }
  }

  if (!descriptionElement) return;
  await nextTick();

  const defTitle = document.getElementById('footer-definition-title') as HTMLDivElement;
  if (!label) { label =  document.createElement('span');label.textContent = 'NOTFOUND'}
      const labelclone = label.cloneNode(true) as HTMLElement;
      labelclone.style.fontWeight = 'bold';
            labelclone.style.color ='white';
      labelclone.style.justifyContent ='center';
      labelclone.innerHTML = labelclone.innerText + ":";

  if (!showDefinition.value) {
    defTitle.style.backgroundColor = '#2ec2d0';
    setTimeout(() => {
      defTitle.style.backgroundColor = '#388087';
    }, 1000);
  }

  const definitionsPanel = document.getElementById('definitions-panel')!;
  definitionsPanel.innerHTML = '';
  definitionsPanel.appendChild(labelclone);
  definitionsPanel.appendChild(descriptionElement.cloneNode(true));
}

function isFrozen() {
  let frozenAccessLevels: Ref<ReadonlyMap<string, boolean>> =
    currentCaseService.getFrozenAccessLevels();
  //TODO: watch and ref instead of function
  let isFrozen = false;
  frozenAccessLevels.value.forEach((freezeValue: boolean) => {
    if (freezeValue) isFrozen = true;
  });
  return isFrozen;
}

function unfreezeCase() {
  const currentCaseID = currentCaseService.getCurrentCase().value;
  if (!currentCaseID) {
    console.log(
      'Cannot unfreeze a case: no case selected in currentCaseService!'
    );
    return;
  }
  caseService.unfreeze(currentCaseID);
  // TODO - this is necesary to reflect the changes to freeze state, remove this lines when
  // https://gitlab.com/dan-de-lion/dandelion-core/-/issues/913 is released
  currentCaseService.terminateEditing();
  currentCaseService.open(currentCaseID);
}

function checkIfDebug() {
  if (isDebug) {
    const intervalId = setInterval(() => {
      const sccContent = document.querySelector(
        '.single-case-component-content'
      );
      if (sccContent) {
        sccContent.classList.add('debug-view');
        clearInterval(intervalId);
      }
    }, 1000);
  }
}

function tryToTakeLock() {
  //TODO: maybe change when Core version == 1.0.0
  const openToken = getContext(Contexts.DEFAULT).addEventListener(ApplicationEvents.CaseOpenedForEditing, () => {
    lockExpired.value = false;
    name.value = model.core?.personalData?.name;
    surname.value = model.core?.personalData?.surname;
    getContext(Contexts.DEFAULT).removeEventListenerByToken(openToken);
  })
  currentCaseService.open(route.params.id as string);
}

onMounted(() => {
  // setTimeout(() => {
  //   const menuItems = document.querySelectorAll("[id^='tree-component-content'] ul > li");
  //   if (!menuItems?.length || menuItems.length < 2) {
  //     return;
  //   }
  //   menuItems[1].classList.add('active');
  //   menuItems.forEach(item => {
  //     item.addEventListener('click', () => {
  //       // Remove 'active' class from all items
  //       menuItems.forEach(i => i.classList.remove('active'));
  //       // Add 'active' class to clicked item
  //       item.classList.add('active');
  //     });
  //   });
  // }, 3000)
  setTimeout(() => checkIfDebug(), 2000);
  varHoverEvent = getContext(Contexts.DEFAULT).addEventListener(
    ApplicationEvents.VariableHovered,
    async (_m, e) => {
      const hovered = e as VariableHovered;
      await changeDescription(hovered.variableName);
    }
  );
  watchStopHandler = watch(
    model,
    debounce(() => validateInputs(), 1000)
  );
  caseUpdatedToken = getContext(Contexts.DEFAULT).addEventListener(
    ApplicationEvents.CaseUpdated,
    async (_m: string, e: Event) => {
      const event = e as CaseUpdated;
      const caseStorage = container.get(
        NamedBindings.CaseStorage
      ) as RESTCaseStorage;
      await caseStorage.lock(event.caseForUpdate.caseID);
      if (event.containerType != ContainerTypes.REEVALUATION) {
        logger.logSuccess(
          ts.t(Messages.CASE_UPDATED) +
            (event.caseForUpdate.caseID?.substring(0, 14) ?? ''),
          {}
        );
      }
    }
  );
  caseUpdatedFailedToken = getContext(Contexts.DEFAULT).addEventListener(
    ApplicationEvents.CaseUpdateFailed,
    async (_m: string, e: Event) => {
      const event: CaseUpdateFailed = e as CaseUpdateFailed;
      logger.logError(
        ts.t(Messages.CASE_UPDATED_FAILED) +
          (event.caseID?.substring(0, 14) ?? ''),
        {}
      );
    }
  );
  caseClosedToken = getContext(Contexts.DEFAULT).addEventListener(
    ApplicationEvents.CaseClosed,
    async (_m: string, e: Event) => {
      const event: CaseClosed = e as CaseClosed;
      if (event.containerType === ContainerTypes.DEFAULT) {
        const caseStorage = container.get(
          NamedBindings.CaseStorage
        ) as CaseStorageInterface;
        await caseStorage.unlock(event.caseID);
        await nextTick(() => router.push(`/`));
      }
    }
  );
  caseLockExpiringSoon = getContext(Contexts.DEFAULT).addEventListener(
    ApplicationEvents.CaseLockExpiringSoon,
    () => {
      setTimeout(
        () => logger.logWarning(ts.t(Messages.LOCK_IS_EXPIRING), {}),
        100
      );
    }
  );
  caseLockExpired = getContext(Contexts.DEFAULT).addEventListener(
    ApplicationEvents.CaseLockExpired,
    () => {
      lockExpired.value = true;
      currentCaseService.isOpenedReadonly.value = true;
    }
  );
});

onBeforeUnmount(() => {
  getContext(Contexts.DEFAULT).removeEventListenerByToken(varHoverEvent);
  getContext(Contexts.DEFAULT).removeEventListenerByToken(caseUpdatedToken);
  getContext(Contexts.DEFAULT).removeEventListenerByToken(caseUpdatedFailedToken);
  getContext(Contexts.DEFAULT).removeEventListenerByToken(caseClosedToken);
  getContext(Contexts.DEFAULT).removeEventListenerByToken(caseLockExpiringSoon);
  getContext(Contexts.DEFAULT).removeEventListenerByToken(caseLockExpired);
  watchStopHandler();
});

const isCaseOpened = currentCaseService.isCaseOpened;
</script>

<template>
  <section id="scc">
    <h4 class="patientid pb-2">
      <ion-icon icon="person-outline"></ion-icon>
      <span v-html="`${ts.t('Patient')}:`"></span>
      <b v-html="`${name} ${surname}`"></b>
    </h4>
    <div id="view-case">
      <section id="utils-buttons-section">
        <StatusDisplayer
          :map-for-status="mapForStatus"
          :status-expanded="isStatusExpanded"
          :class="{ 'full-expanded': isFullscreen && isStatusExpanded }"
          @toggleStatusDisplayer="toggleStatusDisplayer"
          v-if="isCaseOpened"
        ></StatusDisplayer>
        <!-- Toggle Button for Sidebar -->
        <button
          id="sidebar-btn"
          class="toggle-button"
          @click="toggleFullscreen()"
        >
          <ion-icon icon="expand-outline"></ion-icon>
        </button>
        <div id="lock-expires-wrapper">
          <div id="lock-circle-wrapper" v-if="lockCountdown">
            <div class="lock-circle">
              <svg viewBox="0 0 40 40">
                <circle class="background-ring" cx="20" cy="20" r="17" />
                <circle
                  class="countdown-ring"
                  cx="20"
                  cy="20"
                  r="17"
                  :stroke-dasharray="100"
                  :stroke-dashoffset="100 - lockCountdownProgress"
                />
              </svg>
              <span
                class="countdown-text"
                v-html="
                  `${lockCountdown < 10 ? '00:0' : '00:'}${lockCountdown}`
                "
              />
            </div>
            <span
              class="lock-label"
              v-html="`${ts.t('until the lock is expired')}`"
            />
          </div>
          <div id="lock-expired-wrapper" v-else-if="lockExpired">
            <span class="lock-label" v-html="ts.t(Messages.LOCK_IS_EXPIRED)" />
            <button @click="tryToTakeLock()">Sblocca</button>
          </div>
        </div>
      </section>
      <!-- Sidebar -->
      <aside class="sidebar" :class="{ 'sidebar-close': isFullscreen }">
        <TreeComponent
          :show-dataclass-instances-in-tree="true"
          :show-groups-in-tree="true"
          :show-sets-in-tree="true"
          :max-depth="15"
        >
        </TreeComponent>
      </aside>
      <main
        class="main-content"
        :class="{
          'main-content-full': isFullscreen,
          'main-reduced': isStatusExpanded,
        }"
      >
        <section class="case-frozen-message" v-if="isFrozen()">
          <span
            v-html="
              ts.t(
                'The case is frozen because all data has been completed and saved'
              )
            "
          ></span>
          <button v-html="ts.t('Unfreeze')" @click="unfreezeCase()"></button>
        </section>
        <SingleCaseComponent
          class="card w-full p-4 bg-white shadow-xl"
          default-page="core.personalData"
        >
        </SingleCaseComponent>
      </main>
    </div>
    <!-- Notification Displayer-->
    <div
      class="footer-item footer-missing"
      :class="{ 'translate-title-footer': showMissing }"
      @mouseover="showMissing = true"
      @mouseleave="showMissing = false"
    >
      <h4 class="footer-title">
        <ion-icon name="close-circle"></ion-icon>
        <MissingListCounter />
      </h4>
    </div>
    <MissingsListComponent
      :status="3"
      :class="{ 'show-footer': showMissing }"
      @mouseover="showMissing = true"
      @mouseleave="showMissing = false"
      class="footer-component"
    >
    </MissingsListComponent>

    <div
      class="footer-item footer-warning"
      :class="{ 'translate-title-footer': showWarnings }"
      @mouseover="showWarnings = true"
      @mouseleave="showWarnings = false"
    >
      <h4 class="footer-title">
        <ion-icon name="warning"></ion-icon>
        <span v-html="`${ts.t('Warnings')}:`"></span>
        <span
          id="errorCount"
          v-html="caseStatusService.getWarningCounter().value"
        ></span>
      </h4>
    </div>
    <WarningsListComponent
      id="warning"
      :class="{ 'show-footer': showWarnings }"
      @mouseover="showWarnings = true"
      @mouseleave="showWarnings = false"
      class="footer-component footer-warning"
    >
    </WarningsListComponent>

    <div
      class="footer-item footer-error"
      :class="{ 'translate-title-footer': showErrors }"
      @mouseover="showErrors = true"
      @mouseleave="showErrors = false"
    >
      <h4 class="footer-title">
        <ion-icon name="alert-circle"></ion-icon>
        <span v-html="`${ts.t('Errors')}:`"></span>
        <span
          id="errorCount"
          v-html="caseStatusService.getErrorCounter().value"
        ></span>
      </h4>
    </div>
    <ErrorsListComponent
      id="errors"
      :class="{ 'show-footer': showErrors }"
      @mouseover="showErrors = true"
      @mouseleave="showErrors = false"
      class="footer-component footer-error"
    >
    </ErrorsListComponent>
    <div
      @click="moveDescription()"
      id="footer-definition-title"
      class="footer-item footer-definition"
      :class="{ 'translate-title-footer': showDefinition }"
    >
      <h4 class="footer-title">
        <ion-icon name="document-text"></ion-icon>
        <span v-html="ts.t('Definitions')"></span>
      </h4>
    </div>
    <div
      id="definitions-panel"
      :class="{ 'show-footer': showDefinition }"
      class="footer-component footer-definition"
    ></div>
  </section>
  <TemporaryPluginSvgs></TemporaryPluginSvgs>
</template>

<style scoped>
.footer-item,
.footer-component {
  position: fixed;
  width: 25%;
  color: white;
  text-align: center;
  transition: transform 0.3s ease;
  z-index: 999;
  bottom: 0;
  border-left: 3px solid #f2f2f6;
  border-right: 3px solid #f2f2f6;
  cursor: pointer;
}

.footer-item {
  background-color: #388087;
  padding: 1.25rem;
  border-top-left-radius: 1rem;
  border-top-right-radius: 1rem;
}

.footer-component {
  background-color: #388087e1;
  font-size: 1.1rem;
  padding: 0.6rem;
  height: 12.5rem;
  overflow-y: scroll;
  transform: translatey(100%);
}


#definitions-panel {
  height: fit-content;
  min-height: 12.5rem;
  overflow: hidden;
  transition: 0.3s ease;
  max-height: 200px;
  overflow-y: scroll;
}

.footer-component::-webkit-scrollbar {
  width: 0.75rem;
  background-color: #fff;
  border-radius: 0.5rem;
}

.footer-component::-webkit-scrollbar-track {
  box-shadow: inset 0 0 5px #a6adbb;
}

.footer-component::-webkit-scrollbar-thumb {
  background-color: #388087;
  border-radius: 0.5rem;
}

.footer-warning {
  left: 25%;
}

.footer-error {
  left: 50%;
}

.footer-definition {
  left: 75%;
}

.show-footer {
  transform: translatey(0%) !important;
}

.translate-title-footer {
  transform: translatey(-200px);
}

.patientid {
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
  width: fit-content;
  position: fixed;
  z-index: 999;
  top: 20px;
  left: 20%;

  color: white;
}

#view-case #sidebar-btn {
  position: fixed;
  border: none;
  outline: none;
  background-color: #388087;
  color: #fff;
  min-width: 3rem;
  min-height: 3rem;
  top: 18rem;
  padding: 0;
  box-shadow: 0 2px 4px darkslategray;
  border-radius: 50%;
  z-index: 999;
}

#view-case #sidebar-btn ion-icon {
  min-width: 1.75rem;
  min-height: 1.75rem;
}

#view-case #lock-expires-wrapper {
  position: absolute;
  top: 21.5rem;
  right: 0;
}

#view-case #lock-expires-wrapper #lock-circle-wrapper {
  flex-direction: column;
  display: flex;
  gap: 0.5rem;
}

#view-case #lock-expires-wrapper .lock-circle {
  display: grid;
  width: 4.5vw;
  aspect-ratio: 1;
}

#view-case #lock-expires-wrapper .lock-label {
  font-weight: 600;
  text-align: center;
}

#view-case #lock-expires-wrapper svg {
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
}

#view-case #lock-expires-wrapper .background-ring {
  fill: none;
  stroke: #e5e5e5;
  stroke-width: 4.5;
}

#view-case #lock-expires-wrapper .countdown-ring {
  fill: none;
  stroke: #f56c6c;
  stroke-width: 4.5;
  stroke-linecap: round;
  transition: 1s linear;
}

#view-case #lock-expires-wrapper .countdown-text {
  position: absolute;
  align-self: center;
  justify-self: center;
  font-weight: 600;
  font-size: 1.125rem;
}

#view-case #lock-expires-wrapper #lock-expired-wrapper {
  display: flex;
  padding: 0.5rem 0.25rem;
  background-color: lightcoral;
  border: 2px solid darkred;
  color: darkred;
  border-radius: 0.5rem;
}

.sidebar {
  /* background-color: #c7d8e7;
     color: white;
     background: rgb(0,80,149);
    background: linear-gradient(0deg, rgb(55 134 203) 0%, rgba(199,216,231,1) 57%);*/
  padding: 4px;
  transition: transform 0.3s ease;
  overflow-y: scroll;
  width: 17vw;
  position: fixed;
  top: 4.5rem;
  bottom: 4.5rem;
}

.sidebar-close {
  transform: translateX(-100%);
}

.sidebar::-webkit-scrollbar {
  width: 12px;
}

.set-height {
  min-height: 60vh;
}

.sidebar::-webkit-scrollbar-track {
  box-shadow: inset 0 0 5px #bbbaba;
  background-color: #f6f6f2;
  border-radius: 0.6rem;
}

.sidebar::-webkit-scrollbar-thumb {
  background: #388087;
  border-radius: 0.6rem;
}

.sidebar::-webkit-scrollbar-thumb:hover {
  background: #306e74;
}

.main-content {
  position: absolute;
  width: 76vw;
  left: 18vw;
  transition: 0.3s ease;
  padding: 0;
  transform: translateY(1rem);
}

.main-content-full {
  left: 1vw;
  width: 93vw;
}

.main-content.main-reduced {
  transform: translateY(8rem);
}

#view-case {
  display: flex;
  width: 100%;
  /* height: calc(100vh - 4rem);*/
}

.case-frozen-message {
  padding: 2rem 0;
}

.case-frozen-message button {
  display: block;
  margin: 1rem 0;
  padding: 0;
}
</style>
